"use client";

import Link from "next/link";
import { useTranslations, useLocale } from "next-intl";
import {
  Play,
  MessageSquare,
  Settings,
  Terminal,
  User,
  Key,
  BarChart3,
  Globe,
  Cpu,
  Bot,
  Tags,
  ArrowRight,
  Library,
  ServerCog,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";
import { cn } from "@/lib/utils";

const tools = [
  { key: "runs", icon: Play, href: "/runs", protected: true, color: "text-blue-500" },
  { key: "library", icon: Library, href: "/library", protected: true, color: "text-purple-500" },
  { key: "domains", icon: Globe, href: "/domains", protected: true, color: "text-indigo-500" },
  { key: "aiModels", icon: Cpu, href: "/ai-models", protected: true, color: "text-red-500" },
  { key: "agents", icon: Bot, href: "/agents", protected: true, color: "text-teal-500" },
  { key: "documentKeywords", icon: Tags, href: "/document-keywords", protected: true, color: "text-amber-600" },
  { key: "logs", icon: Terminal, href: "/logs", protected: true, color: "text-emerald-500" },
  { key: "profile", icon: User, href: "/profile", protected: true, color: "text-pink-500" },
  { key: "apiKeys", icon: Key, href: "/api-keys", protected: true, color: "text-cyan-500" },
  { key: "analytics", icon: BarChart3, href: "/analytics", protected: true, color: "text-yellow-500" },
  { key: "workers", icon: ServerCog, href: "/workers", protected: true, adminOnly: true, color: "text-slate-600" },
];

export function ToolsGrid() {
  const locale = useLocale();
  const t = useTranslations("tools");
  const { isAuthenticated, hasCapability } = useAuth();

  return (
    <section id="tools-section" className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">{t("quickReference")}</h2>
        <p className="text-sm text-muted-foreground mt-1">{t("intro")}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {tools.map((tool) => {
          // Capability-based, not role-name-based (Phase 6 of
          // [[rbac-permission-overhaul-initiative]]) — matches the
          // sidebar/command-palette, which already gate the Workers nav
          // item on workers.view rather than the "admin" role name.
          if (tool.adminOnly && !hasCapability("workers.view")) return null;
          const Icon = tool.icon;
          const toolT = (key: string) => t(`${tool.key}.${key}` as "runs.name");
          const isLocked = tool.protected && !isAuthenticated;

          return (
            <Card
              key={tool.key}
              className={cn(
                "group relative transition-all hover:shadow-md",
                isLocked && "opacity-60"
              )}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className={cn("p-2 rounded-lg bg-muted", tool.color)}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <CardTitle className="text-base">{toolT("name")}</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <CardDescription className="text-sm line-clamp-3">
                  {toolT("desc")}
                </CardDescription>
                <Button
                  asChild={!isLocked}
                  variant="ghost"
                  size="sm"
                  className="w-full justify-between group-hover:bg-accent"
                  disabled={isLocked}
                >
                  {isLocked ? (
                    <span>
                      {toolT("action")}
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  ) : (
                    <Link href={`/${locale}${tool.href}`}>
                      {toolT("action")}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </section>
  );
}
